import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { mockProjects } from "@/data/mockProjects";
import { mockAssets } from "@/data/mockAssets";
import { mockAgentRuns } from "@/data/mockRuns";
import { mockSkills } from "@/data/mockSkills";
import { mockBrandProfile } from "@/data/mockBrand";
import { mockIntegrations } from "@/data/mockIntegrations";
import { mockCalendarItems } from "@/data/mockCalendar";
import { mockInboxEvents, mockIngestionEndpoint } from "@/data/mockInbox";
import { generateRunAssets } from "@/data/runAssetGenerator";
import { mockBlogs } from "@/data/mockBlogs";
import { createInitialModelSettings, saveProviderCredential, updateModelDefaults, applyBackendCredential, applyBackendProviderList, applyBackendModelDefaults, applyProviderTestResult, setProviderEnabled, clearProviderCredential } from "@/lib/modelProviderSettings";
import { attachImagePromptsForRuns, attachRunImagePrompt, getRunCoverPrompt } from "@/lib/imagePromptAssets";
import { createTemplateFromDraft, deleteCreateTemplate as removeCreateTemplateFromList, upsertCreateTemplate } from "@/lib/createTemplates";
import { normalizeRecipeDiscovery } from "@/lib/discoveryOptions";
import { imageAspectForPlatforms, shouldAttachCoverToAsset } from "@/lib/mediaAspect";
import { finalizeFailedRemoteRun, finalizeRemoteRun, mergeRemoteRun } from "@/lib/runState";
import { clearTenantResources } from "@/lib/tenantResources";
import { useAuth } from "@/state/AuthProvider";
import {
  assetsApi,
  blogsApi,
  calendarApi,
  brandApi,
  brandsApi,
  imagesApi,
  inboxApi,
  persist,
  persistAssetPatch,
  pickCalendarFields,
  pickBlogFields,
  projectsApi,
  providersApi,
  runsApi,
  skillsApi,
} from "@/lib/apiClient";

const LS_KEY = "adams_ai_mock_state_v2";

const RUN_POLL_INTERVAL_MS = 1200;
const RUN_POLL_LIMIT = 300;

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function mergeAssetsById(existing = [], incoming = []) {
  const prepared = attachRunImagePrompt(incoming || []);
  if (!prepared.length) return existing;
  const seen = new Set(prepared.map((asset) => asset.id));
  return [...prepared, ...existing.filter((asset) => !seen.has(asset.id))];
}

const seed = () => ({
  projects: mockProjects,
  assets: mockAssets,
  runs: mockAgentRuns,
  skills: mockSkills,
  brand: mockBrandProfile,
  brands: [],            // multi-brand: [{id, name, industry, is_default, profile, voices}]
  activeBrandId: null,   // which brand the operator is working in
  integrations: mockIntegrations,
  calendar: mockCalendarItems,
  inbox: mockInboxEvents,
  ingestion: mockIngestionEndpoint,
  blogs: mockBlogs,
  modelSettings: createInitialModelSettings(),
  createTemplates: [],
});

const AppStateContext = createContext(null);

export function AppStateProvider({ children }) {
  const { token } = useAuth();
  const [state, setState] = useState(() => {
    if (typeof window === "undefined") return seed();
    try {
      const raw = window.localStorage.getItem(LS_KEY);
      if (raw) return JSON.parse(raw);
    } catch (_) { /* ignore */ }
    return seed();
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify(state));
    } catch (_) { /* ignore quota */ }
  }, [state]);

  // Always-current snapshot of state, readable synchronously inside callbacks
  // (setState updaters run during render, so they can't be used to drive a
  // post-update side effect like "persist the calendar item I just created").
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  // Hydrate tenant resources from the backend. In authenticated production the
  // backend is authoritative: mock/local resources are cleared before hydration,
  // and a failed list call becomes an empty collection, never prototype content.
  useEffect(() => {
    // No session → nothing to hydrate. Clear any prior tenant's data so a
    // logged-out (or just-switched) browser never shows another account's work.
    if (!token) {
      setState((s) => clearTenantResources(s));
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setState((s) => clearTenantResources(s));
    const hydrate = (key, promise) =>
      promise
        .then((rows) => {
          if (Array.isArray(rows)) {
            setState((s) => ({ ...s, [key]: rows }));
          }
        })
        .catch(() => {
          if (!signal.aborted) setState((s) => ({ ...s, [key]: [] }));
        });

    hydrate("projects", projectsApi.list(signal));
    hydrate("calendar", calendarApi.list(signal));
    hydrate("skills", skillsApi.list(signal));
    hydrate("inbox", inboxApi.list(signal));
    hydrate("blogs", blogsApi.list(signal));

    Promise.all([assetsApi.list(signal), runsApi.list(signal)])
      .then(([assets, runs]) => {
        if (signal.aborted) return;
        const safeAssets = Array.isArray(assets) ? assets : [];
        const safeRuns = Array.isArray(runs) ? runs : [];
        setState((s) => ({
          ...s,
          assets: attachImagePromptsForRuns(safeAssets, safeRuns),
          runs: safeRuns,
        }));
      })
      .catch(() => {
        if (!signal.aborted) setState((s) => ({ ...s, assets: [], runs: [] }));
      });

    // Ingestion endpoint info (webhook URL + masked key) is a singleton object.
    inboxApi
      .ingestion(signal)
      .then((info) => {
        if (info && typeof info === "object" && info.webhook_url) {
          setState((s) => ({ ...s, ingestion: { ...s.ingestion, ...info } }));
        }
      })
      .catch(() => { /* keep mock ingestion info */ });

    // Brand profile (active brand) is a singleton object (not an array): merge.
    brandApi
      .get(signal)
      .then((profile) => {
        if (profile && typeof profile === "object" && Object.keys(profile).length > 0) {
          setState((s) => ({ ...s, brand: { ...s.brand, ...profile } }));
        }
      })
      .catch(() => { /* keep local brand profile */ });

    // Multi-brand: the full list of brands (each with its voices) + active brand.
    Promise.all([brandsApi.list(signal), brandsApi.active(signal).catch(() => null)])
      .then(([brands, active]) => {
        if (Array.isArray(brands) && brands.length > 0) {
          setState((s) => ({
            ...s,
            brands,
            activeBrandId: active?.id || s.activeBrandId || brands.find((b) => b.is_default)?.id || brands[0].id,
          }));
        }
      })
      .catch(() => { /* keep local */ });

    // Provider credentials merge into modelSettings rather than replacing a key.
    providersApi
      .list(signal)
      .then((creds) => {
        if (Array.isArray(creds) && creds.length > 0) {
          setState((s) => ({ ...s, modelSettings: applyBackendProviderList(s.modelSettings, creds) }));
        }
      })
      .catch(() => { /* keep local provider settings */ });

    providersApi
      .defaults(signal)
      .then((defaults) => {
        if (defaults && typeof defaults === "object") {
          setState((s) => ({ ...s, modelSettings: applyBackendModelDefaults(s.modelSettings, defaults) }));
        }
      })
      .catch(() => { /* keep local model defaults */ });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ---------- Asset operations ----------
  const updateAsset = useCallback((id, patch) => {
    setState((s) => ({
      ...s,
      assets: s.assets.map((a) => (a.id === id ? { ...a, ...patch, updated_at: new Date().toISOString() } : a)),
    }));
    persistAssetPatch(id, patch);
  }, []);

  const approveAsset = useCallback((id) => updateAsset(id, { status: "approved" }), [updateAsset]);
  const rejectAsset = useCallback((id) => updateAsset(id, { status: "archived" }), [updateAsset]);
  // Real LLM revise/regenerate of a single post via the backend.
  const reviseAsset = useCallback(async (id, { instruction = "", mode = "revise" } = {}) => {
    try {
      const updated = await assetsApi.revise(id, { instruction, mode });
      setState((s) => ({
        ...s,
        assets: s.assets.map((a) => {
          if (a.id !== id) return a;
          // Record a revision-history entry so the turn is auditable/revertable.
          // Prefer the backend's history if it returns one; otherwise build it here.
          const entry = {
            id: `rev_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            created_at: new Date().toISOString(),
            instruction: instruction || (mode === "regenerate" ? "Fresh take — a new angle" : "Revision"),
            mode: mode === "regenerate" ? "regenerate" : "revise",
            before: { hook: a.hook, body: a.body, cta: a.cta },
            after: {
              hook: updated.hook ?? a.hook,
              body: updated.body ?? a.body,
              cta: updated.cta ?? a.cta,
            },
            summary: mode === "regenerate" ? "Generated a fresh variation" : "Applied requested revision",
          };
          // Always append our client-side entry (the backend doesn't track the
          // frontend's revision-history shape), and keep it last so the spread
          // of `updated` can't clobber it.
          const revision_history = [...(a.revision_history || []), entry];
          return { ...a, ...updated, revision_history };
        }),
      }));
      return { ok: true, asset: updated };
    } catch (err) {
      return { ok: false, message: err.message };
    }
  }, []);

  // Revert to the `before` snapshot captured in a revision-history entry. The
  // revert is itself logged as a new entry, so it's auditable and revertable too.
  const revertAsset = useCallback((id, entryId) => {
    setState((s) => ({
      ...s,
      assets: s.assets.map((a) => {
        if (a.id !== id) return a;
        const history = Array.isArray(a.revision_history) ? a.revision_history : [];
        const entry = history.find((e) => e.id === entryId);
        if (!entry?.before) return a;
        const before = entry.before;
        const hashtags = Array.isArray(a.hashtags) ? a.hashtags : [];
        const char_count = [before.hook, before.body, before.cta, hashtags.join(" ")].filter(Boolean).join("\n").length;
        const revertEntry = {
          id: `rev_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          created_at: new Date().toISOString(),
          instruction: `Reverted to the version before "${entry.instruction || entry.mode || "this change"}"`,
          mode: "revert",
          before: { hook: a.hook, body: a.body, cta: a.cta },
          after: { hook: before.hook, body: before.body, cta: before.cta },
          summary: "Reverted to an earlier version",
        };
        return {
          ...a,
          hook: before.hook,
          body: before.body,
          cta: before.cta,
          char_count,
          status: "needs_review",
          revision_history: [...history, revertEntry],
        };
      }),
    }));
    return { ok: true };
  }, []);

  const scheduleAsset = useCallback((id, scheduledAt, metadata = {}) => {
    const localCalId = `cal_${Math.random().toString(36).slice(2, 8)}`;
    setState((s) => {
      const asset = s.assets.find((a) => a.id === id);
      if (!asset) return s;
      const stamp = new Date().toISOString();
      const newAsset = { ...asset, ...metadata, status: "scheduled", schedule_at: scheduledAt, updated_at: stamp };
      const newAssets = s.assets.map((a) => (a.id === id ? newAsset : a));
      const existingCal = s.calendar.find((c) => c.asset_id === id);
      let newCalendar;
      if (existingCal) {
        newCalendar = s.calendar.map((c) => (c.asset_id === id ? { ...c, ...metadata, scheduled_at: scheduledAt, status: "scheduled" } : c));
      } else {
        newCalendar = [
          ...s.calendar,
          { id: localCalId, asset_id: id, platform: asset.platform, title: asset.title, scheduled_at: scheduledAt, status: "scheduled", ...metadata },
        ];
      }
      return { ...s, assets: newAssets, calendar: newCalendar };
    });

    persistAssetPatch(id, { status: "scheduled", schedule_at: scheduledAt });

    // Persist the calendar side using the snapshot from before this update.
    const prev = stateRef.current;
    const asset = prev.assets.find((a) => a.id === id);
    const existingCal = prev.calendar.find((c) => c.asset_id === id);
    if (existingCal) {
      persist(
        () => calendarApi.patch(existingCal.id, pickCalendarFields({ ...metadata, scheduled_at: scheduledAt, status: "scheduled" })),
        "calendar update",
      );
    } else if (asset) {
      persist(async () => {
        const created = await calendarApi.create(
          pickCalendarFields({ asset_id: id, platform: asset.platform, title: asset.title, scheduled_at: scheduledAt, status: "scheduled", ...metadata }),
        );
        // Swap the optimistic local id for the backend-assigned one.
        setState((s) => ({ ...s, calendar: s.calendar.map((c) => (c.id === localCalId ? created : c)) }));
      }, "calendar create");
    }
  }, []);

  const publishCalendarItem = useCallback((calId) => {
    setState((s) => {
      const item = s.calendar.find((c) => c.id === calId);
      if (!item) return s;
      const stamp = new Date().toISOString();
      const newCalendar = s.calendar.map((c) => (c.id === calId ? { ...c, status: "published", published_at: stamp } : c));
      const newAssets = item.asset_id
        ? s.assets.map((a) => (a.id === item.asset_id ? { ...a, status: "published", published_at: stamp, updated_at: stamp } : a))
        : s.assets;
      return { ...s, calendar: newCalendar, assets: newAssets };
    });
    persist(() => calendarApi.publish(calId), "calendar publish");
  }, []);

  // Arm/disarm a calendar item for scheduled auto-publish (publish_mode).
  const armCalendarItem = useCallback((calId, autoOn) => {
    const mode = autoOn ? "auto" : "manual";
    setState((s) => ({
      ...s,
      calendar: s.calendar.map((c) => (c.id === calId ? { ...c, publish_mode: mode } : c)),
    }));
    persist(() => calendarApi.patch(calId, { publish_mode: mode }), "calendar arm");
  }, []);

  const addAsset = useCallback((asset) => {
    setState((s) => ({ ...s, assets: [asset, ...s.assets] }));
    persist(() => assetsApi.create(asset), "asset create");
  }, []);

  // Generate an image (default: free no-key provider; pass providerId "gemini"
  // to use a BYOK provider). Returns { ok, url, message }. Does not attach it
  // anywhere — the caller decides where the URL goes.
  const generateImage = useCallback(async (prompt, providerId, aspect) => {
    try {
      const result = await imagesApi.generate({ prompt, provider_id: providerId || undefined, aspect: aspect || undefined });
      if (result?.url) return { ok: true, url: result.url };
      return { ok: false, message: "No image returned." };
    } catch (err) {
      return { ok: false, message: err.message };
    }
  }, []);

  // Generate a cover for an asset and set it (the platform preview renders it).
  const generateAssetCover = useCallback(async (id, prompt, providerId, aspect) => {
    try {
      const result = await imagesApi.generate({ prompt, asset_id: id, provider_id: providerId || undefined, aspect: aspect || undefined });
      if (result?.url) {
        setState((s) => ({
          ...s,
          assets: s.assets.map((a) => (a.id === id ? { ...a, cover_image: result.url, updated_at: new Date().toISOString() } : a)),
        }));
        return { ok: true, url: result.url };
      }
      return { ok: false, message: "No image returned." };
    } catch (err) {
      return { ok: false, message: err.message };
    }
  }, []);

  // Upload an operator-supplied image (data: URL) and get back a served URL,
  // without binding it to a specific asset. Used where one cover is shared
  // across several posts (agent run / project hub).
  const uploadImage = useCallback(async (dataUrl) => {
    try {
      const result = await imagesApi.upload({ data_url: dataUrl });
      if (result?.url) return { ok: true, url: result.url };
      return { ok: false, message: "No image returned." };
    } catch (err) {
      return { ok: false, message: err.message };
    }
  }, []);

  // Upload an operator-supplied image (data: URL) as an asset's cover. Mirrors
  // generateAssetCover but persists the user's own file instead of generating.
  const uploadAssetCover = useCallback(async (id, dataUrl) => {
    try {
      const result = await imagesApi.upload({ data_url: dataUrl, asset_id: id });
      if (result?.url) {
        setState((s) => ({
          ...s,
          assets: s.assets.map((a) => (a.id === id ? { ...a, cover_image: result.url, updated_at: new Date().toISOString() } : a)),
        }));
        return { ok: true, url: result.url };
      }
      return { ok: false, message: "No image returned." };
    } catch (err) {
      return { ok: false, message: err.message };
    }
  }, []);

  // Publish an asset via the backend (Composio). Returns the result object
  // { ok, fallback, content, message } so the caller can handle the manual
  // fallback (copy-to-clipboard) when publishing isn't available.
  const publishAsset = useCallback(async (id, authorId) => {
    try {
      const result = await assetsApi.publish(id, authorId);
      if (result?.ok) {
        setState((s) => ({
          ...s,
          assets: s.assets.map((a) => (a.id === id ? { ...a, status: "published", updated_at: new Date().toISOString() } : a)),
        }));
      }
      return result;
    } catch (err) {
      return { ok: false, fallback: true, message: err.message };
    }
  }, []);

  // Bulk operations on a list of asset ids
  const bulkUpdateAssets = useCallback((ids, patch) => {
    setState((s) => ({
      ...s,
      assets: s.assets.map((a) => (ids.includes(a.id) ? { ...a, ...patch, updated_at: new Date().toISOString() } : a)),
    }));
    ids.forEach((id) => persistAssetPatch(id, patch));
  }, []);

  const bulkApprove = useCallback((ids) => bulkUpdateAssets(ids, { status: "approved" }), [bulkUpdateAssets]);
  const bulkArchive = useCallback((ids) => bulkUpdateAssets(ids, { status: "archived" }), [bulkUpdateAssets]);

  const bulkSchedule = useCallback((ids, scheduledAt) => {
    // Pre-assign a local calendar id per asset so we can reconcile after the
    // backend create returns its own id.
    const localCalIdByAsset = Object.fromEntries(
      ids.map((id) => [id, `cal_${Math.random().toString(36).slice(2, 8)}`]),
    );
    setState((s) => {
      const idSet = new Set(ids);
      const stamp = new Date().toISOString();
      const newAssets = s.assets.map((a) => (idSet.has(a.id) ? { ...a, status: "scheduled", schedule_at: scheduledAt, updated_at: stamp } : a));
      const existingByAssetId = new Map(s.calendar.filter((c) => c.asset_id && idSet.has(c.asset_id)).map((c) => [c.asset_id, c]));
      const newCalendarFromExisting = s.calendar.map((c) => (c.asset_id && idSet.has(c.asset_id) ? { ...c, scheduled_at: scheduledAt, status: "scheduled" } : c));
      const fresh = ids
        .filter((id) => !existingByAssetId.has(id))
        .map((id) => {
          const a = s.assets.find((x) => x.id === id);
          if (!a) return null;
          return { id: localCalIdByAsset[id], asset_id: id, platform: a.platform, title: a.title, scheduled_at: scheduledAt, status: "scheduled" };
        })
        .filter(Boolean);
      return { ...s, assets: newAssets, calendar: [...newCalendarFromExisting, ...fresh] };
    });

    ids.forEach((id) => persistAssetPatch(id, { status: "scheduled", schedule_at: scheduledAt }));

    // Persist the calendar side using the pre-update snapshot.
    const prev = stateRef.current;
    const existingByAssetId = new Map(prev.calendar.filter((c) => c.asset_id).map((c) => [c.asset_id, c]));
    ids.forEach((id) => {
      const existing = existingByAssetId.get(id);
      if (existing) {
        persist(() => calendarApi.patch(existing.id, { scheduled_at: scheduledAt, status: "scheduled" }), "calendar update");
        return;
      }
      const asset = prev.assets.find((a) => a.id === id);
      if (!asset) return;
      const localCalId = localCalIdByAsset[id];
      persist(async () => {
        const created = await calendarApi.create(
          pickCalendarFields({ asset_id: id, platform: asset.platform, title: asset.title, scheduled_at: scheduledAt, status: "scheduled" }),
        );
        setState((s) => ({ ...s, calendar: s.calendar.map((c) => (c.id === localCalId ? created : c)) }));
      }, "calendar create");
    });
  }, []);

  // Reschedule a calendar item to a new datetime (used by drag-drop)
  const rescheduleCalendarItem = useCallback((calId, newDateIso) => {
    setState((s) => {
      const item = s.calendar.find((c) => c.id === calId);
      if (!item) return s;
      const newCalendar = s.calendar.map((c) => (c.id === calId ? { ...c, scheduled_at: newDateIso } : c));
      const newAssets = item.asset_id
        ? s.assets.map((a) => (a.id === item.asset_id ? { ...a, schedule_at: newDateIso, updated_at: new Date().toISOString() } : a))
        : s.assets;
      return { ...s, calendar: newCalendar, assets: newAssets };
    });
    persist(() => calendarApi.reschedule(calId, newDateIso), "calendar reschedule");
  }, []);

  // ---------- Run operations ----------
  const startMockRun = useCallback((cfg) => {
    const id = `run_${Math.random().toString(36).slice(2, 8)}`;
    const run = {
      id,
      project_id: cfg.project_id || null,
      title: cfg.title || "New agent run",
      prompt: cfg.prompt || cfg.title || "",
      grounding: cfg.context || "",
      remote: Boolean(cfg.remote),
      status: "running",
      started_at: new Date().toISOString(),
      finished_at: null,
      model: cfg.model || "anthropic",
      model_provider: cfg.model_provider || cfg.model || "anthropic",
      model_label: cfg.model_label || cfg.model || "Anthropic",
      model_name: cfg.model_name || cfg.model || "claude-sonnet-4",
      model_configured: Boolean(cfg.model_configured),
      platforms: cfg.platforms || [],
      skills_used: cfg.skills_used || [],
      asset_ids: [],
      audience: cfg.audience || "—",
      goal: cfg.goal || "authority",
      research_mode: cfg.research_mode || "sources_only",
      image_mode: cfg.image_mode || "generate",
      image_text_mode: cfg.image_text_mode || cfg.imageTextMode || "auto",
      uploaded_image: cfg.uploaded_image || null,
      steps: [
        { id: "s1", label: "Analyzed request", status: "running", at: new Date().toISOString() },
        { id: "s2", label: "Selected skills", status: "pending" },
        { id: "s3", label: "Researched sources", status: "pending" },
        { id: "s4", label: "Created strategy", status: "pending" },
        { id: "s5", label: "Generated drafts", status: "pending" },
        { id: "s6", label: "Critiqued and revised", status: "pending" },
        { id: "s7", label: "Ready for review", status: "pending" },
      ],
    };
    setState((s) => ({ ...s, runs: [run, ...s.runs] }));
    return id;
  }, []);

  // Real agent run: starts a local "running" run (so the timeline animates),
  // calls the backend to generate with the configured provider, then finalizes
  // with the real assets. Live failures stay failed and never swap in mock output.
  const startAgentRun = useCallback((cfg) => {
    const localId = startMockRun({ ...cfg, remote: true });

    // Resolve to a provider that's actually configured. The requested default
    // (e.g. "anthropic") may have no key — prefer a connected provider, then any
    // configured one, so generation uses the user's real key + chosen model.
    const providers = stateRef.current.modelSettings?.providers || [];
    const byId = (id) => providers.find((p) => p.id === id);
    let providerId = cfg.model_provider;
    let modelName = cfg.model_name;
    const requested = byId(providerId);
    if (!requested || (!requested.configured && !requested.maskedKey)) {
      const chosen =
        providers.find((p) => p.status === "connected" && p.configured && !p.local) ||
        providers.find((p) => p.configured && !p.local);
      if (chosen) {
        providerId = chosen.id;
        modelName = chosen.defaultModel;
      }
    }

    (async () => {
      try {
        const recipeDiscovery = normalizeRecipeDiscovery(cfg);
        const result = await runsApi.create({
          prompt: cfg.prompt,
          platforms: cfg.platforms || [],
          provider_id: providerId || undefined,
          model: modelName || undefined,
          audience: cfg.audience && cfg.audience !== "—" ? cfg.audience : undefined,
          context: cfg.context || undefined,
          source_url: cfg.source_url || undefined,
          source_label: cfg.source_label || undefined,
          research_mode: cfg.research_mode || undefined,
          research_provider: cfg.research_provider || undefined,
          research_focus: cfg.research_focus || undefined,
          brand_id: cfg.brand_id || undefined,
          voice_id: cfg.voice_id || undefined,
          style_skill_id: cfg.style_skill_id || undefined,
          goal: cfg.goal || undefined,
          cta: typeof cfg.cta === "boolean" ? cfg.cta : undefined,
          cta_type: cfg.cta_type || undefined,
          seo: typeof cfg.seo === "boolean" ? cfg.seo : undefined,
          discovery_mode: recipeDiscovery.discoveryMode,
          hashtag_mode: recipeDiscovery.hashtagMode,
          image_mode: cfg.image_mode || cfg.imageMode || "generate",
          image_text_mode: cfg.image_text_mode || cfg.imageTextMode || "auto",
          destination_url: cfg.destination_url || undefined,
          card_style: cfg.card_style || undefined,
          title: cfg.title || undefined,
        });
        let latest = result;
        let backendRun = latest.run;
        let rawAssets = latest.assets || [];
        const backendRunId = backendRun?.id;
        if (!backendRunId) throw new Error("Backend did not return a run id.");

        setState((s) => ({
          ...s,
          assets: mergeAssetsById(s.assets, rawAssets),
          runs: s.runs.map((r) => (r.id === localId
            ? mergeRemoteRun(r, backendRun, rawAssets)
            : r)),
        }));

        for (let attempt = 0; attempt < RUN_POLL_LIMIT && !["completed", "failed"].includes(backendRun.status); attempt += 1) {
          await wait(RUN_POLL_INTERVAL_MS);
          latest = await runsApi.result(backendRunId);
          backendRun = latest.run;
          rawAssets = latest.assets || [];
          setState((s) => ({
            ...s,
            assets: mergeAssetsById(s.assets, rawAssets),
            runs: s.runs.map((r) => (r.id === localId
              ? mergeRemoteRun(r, backendRun, rawAssets)
              : r)),
          }));
        }

        if (backendRun.status === "failed") {
          toast.error("Live generation failed. Check the execution pipeline for the failed phase.");
          return;
        }

        if (backendRun.status !== "completed") {
          throw new Error("Live generation timed out while waiting for the agent run to finish.");
        }

        setState((s) => ({
          ...s,
          assets: mergeAssetsById(s.assets, rawAssets),
          runs: s.runs.map((r) => (r.id === localId
            ? finalizeRemoteRun(r, backendRun, rawAssets)
            : r)),
        }));

        // One shared cover image for the run's posts (per ARCHITECTURE.md), if
        // the user chose "Generate" image mode. Best-effort, runs after text.
        const deliverableAssets = rawAssets.filter((asset) => !["research_brief", "image_prompt", "execution_report"].includes(asset.asset_type));
        const coverAssets = deliverableAssets.filter(shouldAttachCoverToAsset);
        if (cfg.image_mode === "generate" && coverAssets.length > 0) {
          const coverPrompt = getRunCoverPrompt(rawAssets)
            || `A polished, brand-safe social media cover image for this post: ${cfg.prompt}. No text, logos, or watermarks.`;
          generateImage(coverPrompt, undefined, imageAspectForPlatforms(coverAssets.map((asset) => asset.platform))).then((res) => {
            if (!res?.ok) {
              // Best-effort: the text posts still generated. Tell the operator
              // why no cover appeared (e.g. free image service rate-limited).
              toast.error(`Cover image skipped: ${res?.message || "image generation unavailable"}`);
              return;
            }
            const ids = new Set(coverAssets.map((a) => a.id));
            setState((s) => ({ ...s, assets: s.assets.map((a) => (ids.has(a.id) ? { ...a, cover_image: res.url } : a)) }));
            coverAssets.forEach((a) => persistAssetPatch(a.id, { cover_image: res.url }));
          });
        }

        // "Upload" image mode: persist the operator's image to the backend
        // (served by URL) and set it as the shared cover on the run's posts, so
        // it shows in Library/previews exactly like a generated cover.
        if (cfg.image_mode === "upload" && cfg.uploaded_image && coverAssets.length > 0) {
          imagesApi.upload({ data_url: cfg.uploaded_image }).then((res) => {
            if (!res?.url) return;
            const ids = new Set(coverAssets.map((a) => a.id));
            setState((s) => ({ ...s, assets: s.assets.map((a) => (ids.has(a.id) ? { ...a, cover_image: res.url } : a)) }));
            coverAssets.forEach((a) => persistAssetPatch(a.id, { cover_image: res.url }));
          }).catch((err) => {
            toast.error(`Uploaded image not attached: ${err.message}`);
          });
        }
      } catch (err) {
        const message = err?.message || "Live generation failed";
        setState((s) => ({
          ...s,
          runs: s.runs.map((r) => (r.id === localId
            ? finalizeFailedRemoteRun(r, err)
            : r)),
        }));
        toast.error(`Live generation failed: ${message}`);
      }
    })();

    return localId;
  }, [startMockRun, generateImage]);

  const advanceRun = useCallback((id) => {
    setState((s) => {
      let newlyGeneratedAssets = [];
      const runs = s.runs.map((r) => {
        if (r.id !== id) return r;
        if (r.remote) return r;
        const nextIdx = r.steps.findIndex((st) => st.status !== "done");
        if (nextIdx === -1) return { ...r, status: "completed", finished_at: new Date().toISOString() };

        const completingStep = r.steps[nextIdx];
        const steps = r.steps.map((st, idx) => {
          if (idx < nextIdx) return st;
          if (idx === nextIdx) return { ...st, status: "done", at: new Date().toISOString() };
          if (idx === nextIdx + 1) return { ...st, status: "running", at: new Date().toISOString() };
          return st;
        });
        const finished = steps.every((s2) => s2.status === "done");

        // When "Generated drafts" (s5) completes and no assets exist yet → generate platform-appropriate mock posts.
        let updatedRun = { ...r, steps, status: finished ? "completed" : "running", finished_at: finished ? new Date().toISOString() : null };
        if (!r.remote && completingStep?.id === "s5" && (!r.asset_ids || r.asset_ids.length === 0)) {
          newlyGeneratedAssets = generateRunAssets(updatedRun);
          updatedRun = { ...updatedRun, asset_ids: newlyGeneratedAssets.map((a) => a.id) };
        }
        return updatedRun;
      });
      const assets = newlyGeneratedAssets.length > 0 ? [...newlyGeneratedAssets, ...s.assets] : s.assets;
      return { ...s, runs, assets };
    });
  }, []);

  const pauseRun = useCallback((id) => {
    setState((s) => ({
      ...s,
      runs: s.runs.map((run) => (
        run.id === id
          ? {
              ...run,
              status: "waiting_for_clarification",
              steps: run.steps.map((step) => (step.status === "running" ? { ...step, status: "pending" } : step)),
            }
          : run
      )),
    }));
  }, []);

  const stopRun = useCallback((id) => {
    setState((s) => ({
      ...s,
      runs: s.runs.map((run) => (
        run.id === id
          ? {
              ...run,
              status: "failed",
              finished_at: new Date().toISOString(),
              steps: run.steps.map((step) => (step.status === "running" ? { ...step, status: "failed", at: new Date().toISOString() } : step)),
            }
          : run
      )),
    }));
  }, []);

  // ---------- Inbox ----------
  const archiveInbox = useCallback((id) => {
    setState((s) => ({
      ...s,
      inbox: s.inbox.map((e) => (e.id === id ? { ...e, status: "archived" } : e)),
    }));
    persist(() => inboxApi.archive(id), "inbox archive");
  }, []);

  const makeLocalDraftFromEvent = (evt) => ({
    id: `asset_${Math.random().toString(36).slice(2, 8)}`,
    project_id: null,
    title: evt.title,
    platform: evt.platform,
    asset_type: evt.asset_type,
    status: "draft",
    skill_used: "ImportedDraft",
    hook: evt.preview?.split(".")[0] || "—",
    body: evt.preview,
    cta: "—",
    hashtags: [],
    char_count: (evt.preview || "").length,
    char_limit: 3000,
    updated_at: new Date().toISOString(),
    schedule_at: null,
  });

  const promoteInboxToDraft = useCallback(async (id) => {
    // Backend-first so the persisted asset id matches local state. The backend
    // both creates the draft and archives the source event.
    try {
      const newAsset = await inboxApi.promote(id);
      setState((s) => ({
        ...s,
        inbox: s.inbox.map((e) => (e.id === id ? { ...e, status: "archived" } : e)),
        assets: [newAsset, ...s.assets],
      }));
      return;
    } catch (_) {
      // Backend unreachable — fall back to a local-only draft.
    }
    setState((s) => {
      const evt = s.inbox.find((e) => e.id === id);
      if (!evt) return s;
      return {
        ...s,
        inbox: s.inbox.map((e) => (e.id === id ? { ...e, status: "archived" } : e)),
        assets: [makeLocalDraftFromEvent(evt), ...s.assets],
      };
    });
  }, []);

  // ---------- Skills ----------
  const toggleSkill = useCallback((id) => {
    let nextEnabled;
    setState((s) => ({
      ...s,
      skills: s.skills.map((sk) => {
        if (sk.id !== id) return sk;
        nextEnabled = !sk.enabled;
        return { ...sk, enabled: nextEnabled };
      }),
    }));
    if (nextEnabled !== undefined) persist(() => skillsApi.patch(id, { enabled: nextEnabled }), "skill toggle");
  }, []);
  const updateSkill = useCallback((id, patch) => {
    setState((s) => ({ ...s, skills: s.skills.map((sk) => (sk.id === id ? { ...sk, ...patch } : sk)) }));
    const { name, description, phase, enabled, markdown } = patch || {};
    const clean = Object.fromEntries(
      Object.entries({ name, description, phase, enabled, markdown }).filter(([, v]) => v !== undefined),
    );
    if (Object.keys(clean).length > 0) persist(() => skillsApi.patch(id, clean), "skill update");
  }, []);

  // Fires the REAL ingest path (server-side, no raw key in the browser), then
  // refreshes the list so the operator sees the persisted event appear.
  const sendTestInboxPayload = useCallback(async () => {
    const event = await inboxApi.sendTest();
    setState((s) => ({
      ...s,
      inbox: [event, ...s.inbox],
      ingestion: { ...s.ingestion, last_received_at: event.received_at },
    }));
    return event;
  }, []);

  // Rotates the ingestion key on the backend. Returns the FULL key once so the
  // page can show it a single time for copy-into-agent; state keeps only masked.
  const rotateIngestionKey = useCallback(async () => {
    const res = await inboxApi.rotateKey();
    setState((s) => ({
      ...s,
      ingestion: { ...s.ingestion, api_key_masked: res.api_key_masked, key_set: true },
    }));
    return res;
  }, []);
  const addSkill = useCallback((skill) => {
    // Optimistic add, then persist to the backend so the skill survives a
    // refresh AND becomes visible to the generation engine (which looks skills
    // up in the DB by id). The client generates the stable id (sk_custom_<slug>).
    setState((s) => ({
      ...s,
      skills: [skill, ...s.skills.filter((sk) => sk.id !== skill.id)],
    }));
    const { id, name, description, phase, category, enabled, markdown } = skill || {};
    persist(
      () => skillsApi.create({ id, name, description, phase, category, enabled: enabled !== false, markdown }),
      "skill create",
    );
  }, []);

  const removeSkill = useCallback((id) => {
    setState((s) => ({ ...s, skills: s.skills.filter((sk) => sk.id !== id) }));
    persist(() => skillsApi.remove(id), "skill delete");
  }, []);

  // ---------- Brand + voices (multi-brand) ----------
  // updateBrand operates on the ACTIVE brand's profile (back-compat with the
  // Brand Voice editor). Updates local state + the brands[] entry + persists.
  const updateBrand = useCallback((patch) => {
    const id = stateRef.current.activeBrandId;
    let nextProfile = null;
    setState((s) => {
      nextProfile = { ...s.brand, ...patch };
      const brands = (s.brands || []).map((b) =>
        b.id === id ? { ...b, profile: { ...(b.profile || {}), ...patch }, industry: patch.industry ?? b.industry } : b);
      return { ...s, brand: nextProfile, brands };
    });
    if (id) {
      brandsApi.patch(id, { profile: patch, industry: patch.industry }).catch(() => {});
    } else if (nextProfile) {
      brandApi.save(nextProfile, nextProfile.industry).catch(() => {});
    }
  }, []);

  const setActiveBrand = useCallback(async (id) => {
    setState((s) => {
      const b = (s.brands || []).find((x) => x.id === id);
      return { ...s, activeBrandId: id, brand: b ? { ...s.brand, ...(b.profile || {}) } : s.brand };
    });
    try { await brandsApi.activate(id); } catch (_) { /* keep local */ }
  }, []);

  const createBrand = useCallback(async ({ name, industry, profile } = {}) => {
    try {
      const b = await brandsApi.create({ name, industry: industry || null, profile: profile || {} });
      setState((s) => ({ ...s, brands: [...(s.brands || []), b], activeBrandId: b.id, brand: { ...s.brand, ...(b.profile || {}) } }));
      try { await brandsApi.activate(b.id); } catch (_) { /* */ }
      return { ok: true, brand: b };
    } catch (e) { return { ok: false, error: e.message }; }
  }, []);

  // Rename / re-industry a specific brand (used by the Brands page).
  const updateBrandMeta = useCallback(async (id, patch) => {
    try {
      const b = await brandsApi.patch(id, patch);
      setState((s) => ({
        ...s,
        brands: (s.brands || []).map((x) => (x.id === id ? { ...x, ...b } : x)),
        brand: id === s.activeBrandId ? { ...s.brand, ...(b.profile || {}) } : s.brand,
      }));
      return { ok: true, brand: b };
    } catch (e) { return { ok: false, error: e.message }; }
  }, []);

  const deleteBrand = useCallback(async (id) => {
    try {
      await brandsApi.remove(id);
      setState((s) => {
        const brands = (s.brands || []).filter((b) => b.id !== id);
        const nextActive = s.activeBrandId === id ? (brands.find((b) => b.is_default)?.id || brands[0]?.id || null) : s.activeBrandId;
        const activeProfile = brands.find((b) => b.id === nextActive)?.profile || s.brand;
        return { ...s, brands, activeBrandId: nextActive, brand: activeProfile };
      });
      return { ok: true };
    } catch (e) { return { ok: false, error: e.message }; }
  }, []);

  const _reloadVoices = useCallback(async (brandId) => {
    try {
      const voices = await brandsApi.voices(brandId);
      setState((s) => ({ ...s, brands: (s.brands || []).map((b) => (b.id === brandId ? { ...b, voices } : b)) }));
    } catch (_) { /* */ }
  }, []);

  const createVoice = useCallback(async (brandId, body) => {
    try { await brandsApi.createVoice(brandId, body); await _reloadVoices(brandId); return { ok: true }; }
    catch (e) { return { ok: false, error: e.message }; }
  }, [_reloadVoices]);
  const updateVoice = useCallback(async (brandId, vid, body) => {
    try { await brandsApi.updateVoice(vid, body); await _reloadVoices(brandId); return { ok: true }; }
    catch (e) { return { ok: false, error: e.message }; }
  }, [_reloadVoices]);
  const deleteVoice = useCallback(async (brandId, vid) => {
    try { await brandsApi.removeVoice(vid); await _reloadVoices(brandId); return { ok: true }; }
    catch (e) { return { ok: false, error: e.message }; }
  }, [_reloadVoices]);

  // One-click industry pack: fills a brand's defaults + enables industry skills.
  const applyIndustryPack = useCallback(async (packId, brandId) => {
    const id = brandId || stateRef.current.activeBrandId;
    try {
      const res = await brandApi.applyPack(packId, id);
      setState((s) => ({
        ...s,
        brand: id === s.activeBrandId ? { ...s.brand, ...(res.brand || {}) } : s.brand,
        brands: (s.brands || []).map((b) =>
          b.id === id ? { ...b, profile: { ...(b.profile || {}), ...(res.brand || {}) }, industry: (res.brand || {}).industry ?? b.industry } : b),
        skills: Array.isArray(res.skills) && res.skills.length ? res.skills : s.skills,
      }));
      return { ok: true, brand: res.brand };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  }, []);

  // ---------- Integrations ----------
  const toggleIntegration = useCallback((id) => {
    setState((s) => ({
      ...s,
      integrations: s.integrations.map((it) =>
        it.id === id
          ? { ...it, connected: !it.connected, key_configured: it.key_configured || !it.connected, last_checked: new Date().toISOString() }
          : it,
      ),
    }));
  }, []);

  // ---------- Model settings ----------
  const updateModelSettings = useCallback((patch) => {
    setState((s) => ({ ...s, modelSettings: updateModelDefaults(s.modelSettings, patch) }));
    if (patch.defaultProvider !== undefined) {
      providersApi
        .saveDefaults({ default_provider: patch.defaultProvider })
        .then((defaults) => {
          setState((s) => ({ ...s, modelSettings: applyBackendModelDefaults(s.modelSettings, defaults) }));
        })
        .catch(() => { /* backend down - keep optimistic local state */ });
    }
  }, []);

  const saveModelProviderCredential = useCallback(async (providerId, patch) => {
    // Optimistic local update (masks the key immediately).
    setState((s) => ({ ...s, modelSettings: saveProviderCredential(s.modelSettings, providerId, patch) }));
    try {
      const cred = await providersApi.save(providerId, {
        api_key: patch.apiKey || undefined,
        endpoint: patch.endpoint !== undefined ? patch.endpoint : undefined,
        default_model: patch.defaultModel !== undefined ? patch.defaultModel : undefined,
      });
      setState((s) => ({ ...s, modelSettings: applyBackendCredential(s.modelSettings, cred) }));
    } catch (_) {
      /* backend down — keep optimistic local state */
    }
  }, []);

  const testModelProvider = useCallback(async (providerId) => {
    try {
      const result = await providersApi.test(providerId);
      setState((s) => ({ ...s, modelSettings: applyProviderTestResult(s.modelSettings, providerId, result) }));
      return result;
    } catch (err) {
      const result = {
        ok: false,
        status: "error",
        message: err?.message || "Connection test failed",
        last_tested_at: new Date().toISOString(),
      };
      setState((s) => ({ ...s, modelSettings: applyProviderTestResult(s.modelSettings, providerId, result) }));
      return result;
    }
  }, []);

  // Remove a saved key (DELETE) — resets the provider card to unconfigured.
  const removeModelProvider = useCallback(async (providerId) => {
    setState((s) => ({ ...s, modelSettings: clearProviderCredential(s.modelSettings, providerId) }));
    try { await providersApi.remove(providerId); return { ok: true }; }
    catch (e) { return { ok: false, error: e.message }; }
  }, []);

  // Soft on/off — keep the key but stop using the provider.
  const setModelProviderEnabled = useCallback(async (providerId, enabled) => {
    setState((s) => ({ ...s, modelSettings: setProviderEnabled(s.modelSettings, providerId, enabled) }));
    try { const cred = await providersApi.setEnabled(providerId, enabled); setState((s) => ({ ...s, modelSettings: applyBackendCredential(s.modelSettings, cred) })); return { ok: true }; }
    catch (e) { return { ok: false, error: e.message }; }
  }, []);

  // ---------- Create templates ----------
  const saveCreateTemplate = useCallback((draft, options = {}) => {
    const template = createTemplateFromDraft(draft, options);
    setState((s) => ({
      ...s,
      createTemplates: upsertCreateTemplate(s.createTemplates || [], template),
    }));
    return template;
  }, []);

  const deleteCreateTemplate = useCallback((templateId) => {
    setState((s) => ({
      ...s,
      createTemplates: removeCreateTemplateFromList(s.createTemplates || [], templateId),
    }));
  }, []);

  // ---------- Blogs ----------
  const createBlog = useCallback((seed = {}) => {
    const id = `blog_${Math.random().toString(36).slice(2, 8)}`;
    const blog = {
      id,
      title: seed.title || "Untitled blog post",
      slug: (seed.title || "untitled").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "untitled",
      status: "draft",
      cover_image: null,
      seo_title: seed.title || "",
      seo_meta: "",
      excerpt: "",
      tags: [],
      body_html: seed.body_html || "<h1>New blog post</h1><p>Start writing…</p>",
      word_count: 0,
      image_prompt: seed.image_prompt || "",
      author: "Adam",
      updated_at: new Date().toISOString(),
      schedule_at: null,
      published_at: null,
    };
    setState((s) => ({ ...s, blogs: [blog, ...(s.blogs || [])] }));
    // Persist with the same id so local + backend stay aligned.
    persist(() => blogsApi.create({ id, ...pickBlogFields(blog) }), "blog create");
    return id;
  }, []);

  // Coalesce + debounce blog PATCHes — the editor fires updates on every
  // keystroke, so we batch them into one backend write per ~800ms.
  const blogPatchTimers = useRef({});
  const blogPendingPatch = useRef({});
  const updateBlog = useCallback((id, patch) => {
    setState((s) => ({
      ...s,
      blogs: (s.blogs || []).map((b) => (b.id === id ? { ...b, ...patch, updated_at: new Date().toISOString() } : b)),
    }));
    blogPendingPatch.current[id] = { ...(blogPendingPatch.current[id] || {}), ...patch };
    clearTimeout(blogPatchTimers.current[id]);
    blogPatchTimers.current[id] = setTimeout(() => {
      const pending = blogPendingPatch.current[id] || {};
      blogPendingPatch.current[id] = {};
      const clean = pickBlogFields(pending);
      if (Object.keys(clean).length > 0) persist(() => blogsApi.patch(id, clean), "blog update");
    }, 800);
  }, []);

  const deleteBlog = useCallback((id) => {
    setState((s) => ({ ...s, blogs: (s.blogs || []).filter((b) => b.id !== id) }));
    persist(() => blogsApi.remove(id), "blog delete");
  }, []);

  // Real long-form generation via the backend (user's configured provider).
  const generateBlog = useCallback(async (id, { topic, audience, context, source_url, research_mode, research_provider, research_focus, provider_id, model, brand_id, voice_id, style_skill_id, goal, cta, cta_type, seo, discovery_mode, hashtag_mode, discoveryMode, hashtagMode, destination_url, sources } = {}) => {
    const ms = stateRef.current.modelSettings || {};
    const providers = ms.providers || [];
    const defId = ms.defaultProvider;
    // An explicit provider (picked in the blog dialog) wins. Otherwise prefer the
    // workspace default, then any tested-OK provider, then any configured one —
    // never auto-picking a provider that last errored (e.g. a quota-limited key).
    const chosen =
      (provider_id && providers.find((p) => p.id === provider_id && p.configured)) ||
      providers.find((p) => p.id === defId && p.configured && p.status !== "error") ||
      providers.find((p) => p.status === "connected" && p.configured && !p.local) ||
      providers.find((p) => p.configured && p.status !== "error" && !p.local) ||
      providers.find((p) => p.configured && !p.local);
    // Ensure the blog exists on the backend before generating — createBlog's
    // own POST races with this; a 409 just means it's already there.
    const local = (stateRef.current.blogs || []).find((b) => b.id === id);
    if (local) {
      try { await blogsApi.create({ id, ...pickBlogFields(local) }); } catch (_) { /* already exists */ }
    }
    try {
      const recipeDiscovery = normalizeRecipeDiscovery({ seo, discovery_mode, hashtag_mode, discoveryMode, hashtagMode });
      const result = await blogsApi.generate(id, {
        topic: topic || undefined,
        audience: audience || undefined,
        context: context || undefined,
        source_url: source_url || undefined,
        research_mode: research_mode || undefined,
        research_provider: research_provider || undefined,
        research_focus: research_focus || undefined,
        brand_id: brand_id || undefined,
        voice_id: voice_id || undefined,
        style_skill_id: style_skill_id || undefined,
        goal: goal || undefined,
        cta: typeof cta === "boolean" ? cta : undefined,
        cta_type: cta_type || undefined,
        seo: typeof seo === "boolean" ? seo : undefined,
        discovery_mode: recipeDiscovery.discoveryMode,
        hashtag_mode: recipeDiscovery.hashtagMode,
        destination_url: destination_url || undefined,
        sources: sources && sources.length ? sources : undefined,
        provider_id: chosen?.id || undefined,
        model: model || chosen?.defaultModel || undefined,
      });
      setState((s) => ({
        ...s,
        blogs: (s.blogs || []).map((b) => (b.id === id ? { ...b, ...result } : b)),
      }));
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err.message };
    }
  }, []);

  // ---------- Reset ----------
  const resetMockData = useCallback(() => {
    setState((s) => (token ? clearTenantResources(s) : seed()));
  }, [token]);

  const value = useMemo(
    () => ({
      state,
      // assets
      updateAsset, approveAsset, rejectAsset, reviseAsset, revertAsset, scheduleAsset, addAsset, publishAsset, generateAssetCover, uploadAssetCover, uploadImage, generateImage,
      // bulk
      bulkApprove, bulkArchive, bulkSchedule, bulkUpdateAssets,
      // calendar
      rescheduleCalendarItem, publishCalendarItem, armCalendarItem,
      // runs
      startMockRun, startAgentRun, advanceRun, pauseRun, stopRun,
      // inbox
      archiveInbox, promoteInboxToDraft, sendTestInboxPayload, rotateIngestionKey,
      // skills
      toggleSkill, updateSkill, addSkill, removeSkill,
      // brand + voices (multi-brand)
      updateBrand,
      applyIndustryPack,
      setActiveBrand, createBrand, updateBrandMeta, deleteBrand,
      createVoice, updateVoice, deleteVoice,
      // integrations
      toggleIntegration,
      // model settings
      updateModelSettings, saveModelProviderCredential, testModelProvider, removeModelProvider, setModelProviderEnabled,
      // create templates
      saveCreateTemplate, deleteCreateTemplate,
      // blogs
      createBlog, updateBlog, deleteBlog, generateBlog,
      // utility
      resetMockData,
    }),
    [state, updateAsset, approveAsset, rejectAsset, reviseAsset, revertAsset, scheduleAsset, addAsset, publishAsset, generateAssetCover, uploadAssetCover, uploadImage, generateImage, bulkApprove, bulkArchive, bulkSchedule, bulkUpdateAssets, rescheduleCalendarItem, publishCalendarItem, armCalendarItem, startMockRun, startAgentRun, advanceRun, pauseRun, stopRun, archiveInbox, promoteInboxToDraft, sendTestInboxPayload, rotateIngestionKey, toggleSkill, updateSkill, addSkill, removeSkill, updateBrand, applyIndustryPack, setActiveBrand, createBrand, updateBrandMeta, deleteBrand, createVoice, updateVoice, deleteVoice, toggleIntegration, updateModelSettings, saveModelProviderCredential, testModelProvider, removeModelProvider, setModelProviderEnabled, saveCreateTemplate, deleteCreateTemplate, createBlog, updateBlog, deleteBlog, generateBlog, resetMockData],
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState() {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error("useAppState must be used inside <AppStateProvider>");
  return ctx;
}
